/**
 * Tracks whether the user is still in a continuous post-submit wait on the
 * batch processing screen. Used to mark `outputPreviewed.continuousWait` so
 * time-to-first-frame can exclude return visits after the user left.
 *
 * State lives in sessionStorage (per tab) plus a short unmount grace so React
 * Strict Mode remounts / soft remounts do not falsely clear the wait.
 */

const STORAGE_PREFIX = 'cartwheel:batchContinuousWait:';
/** Remounts within this window still count as continuous (Strict Mode, etc.). */
const UNMOUNT_GRACE_MS = 250;

type WaitRecord =
  | { status: 'watching' }
  | { status: 'leaving'; at: number };

function storageKey(batchID: string): string {
  return `${STORAGE_PREFIX}${batchID}`;
}

function readRecord(batchID: string): WaitRecord | null {
  try {
    const raw = sessionStorage.getItem(storageKey(batchID));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WaitRecord;
    if (parsed?.status === 'watching') return parsed;
    if (
      parsed?.status === 'leaving' &&
      typeof parsed.at === 'number' &&
      Number.isFinite(parsed.at)
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeRecord(batchID: string, record: WaitRecord): void {
  try {
    sessionStorage.setItem(storageKey(batchID), JSON.stringify(record));
  } catch {
    /* private mode / quota — TTFF eligibility just degrades to false */
  }
}

function clearRecord(batchID: string): void {
  try {
    sessionStorage.removeItem(storageKey(batchID));
  } catch {
    /* ignore */
  }
}

/** Call right before navigating to the processing screen after a successful submit. */
export function beginBatchContinuousWait(batchID: string): void {
  const id = batchID.trim();
  if (!id) return;
  writeRecord(id, { status: 'watching' });
}

/**
 * Call when the processing screen mounts. Restores a wait that was briefly
 * marked leaving (Strict Mode), or clears a stale leave after the user returned.
 */
export function noteBatchProcessingScreenMounted(batchID: string): void {
  const id = batchID.trim();
  if (!id) return;
  const record = readRecord(id);
  if (!record) return;
  if (record.status === 'watching') return;
  if (Date.now() - record.at <= UNMOUNT_GRACE_MS) {
    writeRecord(id, { status: 'watching' });
    return;
  }
  clearRecord(id);
}

/**
 * Call when the processing screen unmounts. Marks the wait as leaving; a quick
 * remount can restore it, otherwise the next mount clears it.
 */
export function noteBatchProcessingScreenUnmounted(batchID: string): void {
  const id = batchID.trim();
  if (!id) return;
  const record = readRecord(id);
  if (!record || record.status !== 'watching') return;
  writeRecord(id, { status: 'leaving', at: Date.now() });
}

/**
 * Whether this batch is still a continuous post-submit wait.
 * Does not clear state (safe to call before emit).
 */
export function isBatchContinuousWait(batchID: string): boolean {
  const id = batchID.trim();
  if (!id) return false;
  const record = readRecord(id);
  return record?.status === 'watching';
}

/**
 * Read-and-clear continuous-wait eligibility for `outputPreviewed`.
 */
export function consumeBatchContinuousWait(batchID: string): boolean {
  const id = batchID.trim();
  if (!id) return false;
  const eligible = isBatchContinuousWait(id);
  clearRecord(id);
  return eligible;
}
